import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { facebookAuthCallbackRequest } from '../redux/slices/integrationsSlice';
import { ENDPOINTS } from '../api/endPoints';

export const useInstagramAuth = () => {
  const dispatch = useDispatch();

  const startInstagramFacebookAuth = useCallback(async () => {
    const authorizeUrl = ENDPOINTS.integrations.authorize('instagram');
    
    if (!authorizeUrl) {
      throw new Error('Instagram authorization endpoint not configured');
    }

    sessionStorage.setItem('oauth_platform', 'instagram');
    const popup = window.open(authorizeUrl, 'instagram_oauth', 'width=600,height=700');

    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      window.location.href = authorizeUrl;
      return;
    }

    try {
      const result = await waitForAuthMessage('instagram', dispatch);
      return result;
    } catch (err) {
      console.error('Instagram auth failed:', err);
      throw err;
    }
  }, [dispatch]);

  const startInstagramDirectAuth = useCallback(async () => {
    const authorizeUrl = ENDPOINTS.integrations.authorize('instagram-direct');
    
    if (!authorizeUrl) {
      throw new Error('Instagram direct authorization endpoint not configured');
    }
    sessionStorage.setItem('oauth_platform', 'instagram-direct');
    const popup = window.open(authorizeUrl, 'instagram_direct_oauth', 'width=600,height=700');

    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      window.location.href = authorizeUrl;
      return;
    }

    try {
      const result = await waitForAuthMessage('instagram-direct', dispatch);
      return result;
    } catch (err) {
      console.error('Instagram direct auth failed:', err);
      throw err;
    }
  }, [dispatch]);

  return { startInstagramFacebookAuth, startInstagramDirectAuth };
};

function waitForAuthMessage(platform: string, dispatch: any): Promise<any> {
  return new Promise((resolve, reject) => {
    const handler = (event: MessageEvent) => {
      const { type, payload } = event.data || {};

      // Handle both underscore and hyphen formats from backend
      if (type === 'instagram_AUTH_SUCCESS' || 
          type === 'instagram-direct_AUTH_SUCCESS' ||
          type === 'instagram_direct_AUTH_SUCCESS') {
        cleanup();
        sessionStorage.setItem('instagram_auth_just_completed', 'true');
        
        // Dispatch Redux action to call authenticate endpoint with correct platform
        dispatch(facebookAuthCallbackRequest({
          ...payload,
          platform
        }));
        
        resolve(payload);
      }

      if (type === 'instagram_AUTH_ERROR' || 
          type === 'instagram-direct_AUTH_ERROR' ||
          type === 'instagram_direct_AUTH_ERROR') {
        cleanup();
        reject(payload);
      }
    };

    const cleanup = () => {
      window.removeEventListener('message', handler);
      clearTimeout(timeout);
    };

    const timeout = setTimeout(() => {
      cleanup();
      reject('Timeout');
    }, 60000);

    window.addEventListener('message', handler);
  });
}
